import { SyncTransport } from './sync-transport';
import { YjsRegistry } from './yjs-registry';
import { SyncEngine } from './sync-engine';
import { startOpSync, type OpSyncHandle } from './op-sync-bridge';
import type { PersistenceAdapter } from './persistence-adapter';
import { fireDocDeleted } from './doc-deleted-bus';
import { fireDocAccessDenied, type AccessDeniedReason } from './doc-access-denied-bus';
import { capture, type EntityKind } from '@/lib/analytics/capture';
import type { YjsRoomKind } from './yjs-registry';

/** A Yjs room kind is not an analytics entity kind: 'chat' is a conversation
 *  in the product's own vocabulary, and reporting every room as a document
 *  loses canvases and spaces entirely. */
const ANALYTICS_KIND: Record<YjsRoomKind, EntityKind> = {
  document: 'document',
  chat: 'conversation',
  canvas: 'canvas',
  space: 'space',
};
import { CorrelationError } from './correlation-error';
import { getActiveEntityStore, getEntityRow } from './entity-store';
import { canEdit } from '@/lib/sharing/utils/permissions';

/**
 * Whether the store knows this doc and says the user may not edit it. An
 * unknown doc (not yet bootstrapped, or not an entity) is not blocked: the
 * server remains the authority and answers a refused write with read_only.
 */
function mayOnlyRead(id: string): boolean {
  let row;
  try {
    row = getEntityRow(getActiveEntityStore().getState(), id);
  } catch {
    return false;
  }
  return row !== null && !canEdit(row.access);
}

export interface SyncSystem {
  transport: SyncTransport;
  registry: YjsRegistry;
  engine: SyncEngine;
  /**
   * Op sync bridge handle — owns the ops correlation map(s) and exposes
   * sendEphemeralBatch for services that need to submit a one-off op and
   * await the correlated OpResult (e.g. snapshot preview reads).
   */
  opSync: OpSyncHandle;
  /**
   * True once {@link dispose} has run. The SyncSystemProvider checks this during
   * render to recreate a fresh system after a StrictMode/remount teardown —
   * reusing a disposed system would bind consumers to a closed WebSocket.
   */
  disposed: boolean;
  /** Tears down transport, engine listeners, and all registry entries. Safe to call multiple times. */
  dispose(): Promise<void>;
}

export interface SyncSystemOptions {
  /**
   * When true, the client will not send UPDATE frames to the server
   * (writes are suppressed). Awareness frames still flow (so share
   * viewers can broadcast their cursor). Used by share-mode viewers whose
   * server-side permissions are view-only. Design decision #9 — orthogonal
   * to persistence: read-only is an independent capability dimension.
   */
  readOnly?: boolean;
  /**
   * Optional callback fired when a doc's local persistence is ready.
   * The createSyncSystem wiring (sendSubscribe) fires first; this hook fires
   * immediately after. Used by SyncSystemProvider to bridge the syncEventBus.
   */
  onLocalReady?: (id: string) => void;
}

export function createSyncSystem(
  persistence: PersistenceAdapter,
  options: SyncSystemOptions = {},
): SyncSystem {
  const { readOnly = false, onLocalReady: localReadyHook } = options;
  const transport = new SyncTransport();

  // Lazy engine reference — Registry callbacks defer to engine after construction.
  // The ref wrapper is needed because the callbacks capture `engineRef` in a closure,
  // but `engineRef.current` is only assigned after `YjsRegistry` is constructed.
  const engineRef: { current: SyncEngine | null } = { current: null };

  const registry = new YjsRegistry(persistence, {
    onLocalReady: (id) => {
      // sendSubscribe returns a promise that rejects on permission errors,
      // timeout, or other server-side correlation failures. Fire-and-forget
      // callers route permission errors to the docAccessDeniedBus so the
      // UI can surface them; other errors log quietly.
      engineRef.current?.sendSubscribe(id).catch((err: unknown) => {
        if (err instanceof CorrelationError) {
          if (err.code === 'server_error' && (err.serverCode === 'permission_denied' || err.serverCode === 'forbidden')) {
            fireDocAccessDenied(id, err.serverCode as AccessDeniedReason);
            capture('entity_access_denied', { reason: err.serverCode });
            return;
          }
          if (err.code === 'connection_lost') {
            // Reconnect replay will retry — no action needed.
            return;
          }
          if (err.code === 'timeout') {
            console.warn(`[createSyncSystem] subscribe timed out for ${id}`);
            return;
          }
        }
        console.warn(`[createSyncSystem] subscribe failed for ${id}:`, err);
      });
      localReadyHook?.(id);
    },
    // In read-only mode, suppress local update forwarding — the server would
    // reject these with "read_only: Edit permission required" anyway. The
    // same holds for any doc the signed-in user may only read.
    onLocalUpdate: readOnly
      ? () => {}
      : (id, update) => {
          if (mayOnlyRead(id)) return;
          engineRef.current?.sendLocalUpdate(id, update);
        },
    onLocalAwareness: (id, update) => engineRef.current?.sendLocalAwareness(id, update),
    onTornDown: (id) => engineRef.current?.clearPending(id),
    onDocDeleted: (id, actor, kind) => {
      fireDocDeleted(id, actor);
      // Only a delete made elsewhere is worth reporting: a local one is the
      // user's own action, already captured where it was performed.
      if (actor === 'remote') {
        capture('entity_deleted_remote', { entity_kind: ANALYTICS_KIND[kind] });
      }
    },
  });

  const engine = new SyncEngine(transport, registry);
  engineRef.current = engine;

  // Start the op sync bridge here (not in SyncSystemProvider's useEffect) so
  // its lifecycle is tied to the system object and services like
  // SnapshotService can reach sendEphemeralBatch via useSyncSystem().
  const opSync = startOpSync(transport, engine, (after) => {
    void engine.sendSync(after).catch((err) => {
      console.warn('[createSyncSystem] permissionDenied resync failed:', err);
    });
  });

  const system: SyncSystem = {
    transport,
    registry,
    engine,
    opSync,
    disposed: false,
    async dispose() {
      system.disposed = true;
      opSync.stop();
      engine.dispose();
      registry.disposeAll();
      await transport.disconnect();
    },
  };
  return system;
}
